import { forwardRef, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { CountriesPhone, CountriesPhoneList } from 'shared/constants/countries'
import { PHONE_NUMBER } from 'shared/constants/regex'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import {
  PhoneCountry,
  PhoneInputProps,
} from 'shared/ui/phone-input-label/model'

import { ImageLazy } from '../image-lazy'

import { SelectCountrySheet } from './components/select-country-sheet'
import s from './styles.module.scss'

export const PhoneInputWhitLabel = forwardRef<
  HTMLInputElement,
  PhoneInputProps
>(
  (
    { value, error, onChange, variant = 'default', label, disabled = false },
    ref,
  ) => {
    const { t } = useTranslation()
    const { openDialog } = useDialogBottomSheet()

    const localeCountries = useMemo(
      () =>
        CountriesPhoneList.map((country) => ({
          ...country,
          name: t(`country_phone.${country.name}`),
        })),
      [t],
    )

    const [currentPhoneMask, setCurrentPhoneMask] = useState<
      PhoneCountry | undefined
    >(
      localeCountries.find(
        (country) => country.code === (value?.code || CountriesPhone.KG.code),
      ),
    )

    const showBottomSheet = () => {
      if (disabled) return

      openDialog({
        child: (
          <SelectCountrySheet
            setCurrentPhoneMask={(phone) => {
              setCurrentPhoneMask(phone)
              onChange({ code: phone.code, phone: '' })
            }}
            currentCountry={currentPhoneMask}
            localeCountries={localeCountries}
          />
        ),
      })
    }

    const onChangeInputNumber = (phone: string) => {
      const clean = phone
        .replace(PHONE_NUMBER, '')
        ?.slice(0, currentPhoneMask?.length)

      onChange({
        phone: clean,
        code: currentPhoneMask?.code || '',
      })
    }

    return (
      <div className={s.root}>
        <div
          className={clsx(s.container, s[variant], error && s.errorContainer)}
        >
          <div
            className={s.countryCodeField}
            onClick={showBottomSheet}
          >
            <ImageLazy
              alt="flag"
              src={currentPhoneMask?.flag}
              className={s.countryIcon}
            />

            <p className={s.countryCode}>{currentPhoneMask?.dial_code}</p>
          </div>

          <div className={s.inputWrapper}>
            <input
              ref={ref}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={value?.phone || ''}
              placeholder=" "
              disabled={disabled}
              onChange={(e) =>
                onChangeInputNumber(String(e.currentTarget.value))
              }
              className={s.input}
            />

            {label && <label className={s.label}>{label}</label>}
          </div>
        </div>
        {error?.message && <p className={s.error}>{error?.message}</p>}
      </div>
    )
  },
)

PhoneInputWhitLabel.displayName = 'PhoneInputWhitLabel'
